import { useEffect, useState } from "react";
import { getAllWorkTypes, getAllTenderTypes } from "../../../../services/AdminServices";
import { IoClose, IoTrashOutline } from "react-icons/io5";
import { formatCurrency } from "../../../../utility/formatCurrency";
import { useTranslation } from "react-i18next";

const AddStaffTenderProcessForm = ({ onSubmit, editData, onClose, open }) => {
    const {t}=useTranslation();
    const [formData, setFormData] = useState({
        workTypeId: "",
        description: "",
        tenderReferenceNo: "",
        date: "",
        tenderId: "",
        tenderTypeId: "",
        formOfContract: "",
        tenderFee: "",
        tenderValue: "",
        emd: ""
    });

    const [attachmentRows, setAttachmentRows] = useState([
        { id: Date.now(), description: "", file: null, fileName: "" }
    ]);

    const [workTypes, setWorkTypes] = useState([]);
    const [tenderTypes, setTenderTypes] = useState([]);

    useEffect(() => {
        if (open) {
            fetchWorkTypes();
            fetchTenderTypes();
        }
    }, [open]);

    const fetchWorkTypes = async () => {
        try {
            const res = await getAllWorkTypes();
            setWorkTypes(res.data || []);
        } catch (err) {
            console.error("Failed to fetch work types", err);
        }
    };

    const fetchTenderTypes = async () => {
        try {
            const res = await getAllTenderTypes();
            setTenderTypes(res.data || []);
        } catch (err) {
            console.error("Failed to fetch tender types", err);
        }
    };

    useEffect(() => {
        if (editData && open) {
            setFormData({
                workTypeId: editData.workTypeId || editData.workType?.id || "",
                description: editData.description || "",
                tenderReferenceNo: editData.tenderReferenceNo || "",
                date: editData.date || "",
                tenderId: editData.tenderId || "",
                tenderTypeId: editData.tenderTypeId || editData.tenderType?.id || "",
                formOfContract: editData.formOfContract || "",
                tenderFee: editData.tenderFee || "",
                tenderValue: editData.tenderValue || "",
                emd: editData.emd || ""
            });
            if (editData.attachmentData) {
                setAttachmentRows(editData.attachmentData.map(item => ({
                    id: item.id,
                    description: item.description || "",
                    file: null,
                    fileName: item.attachmentName || ""
                })));
            }
        } else {
            resetForm();
        }
    }, [editData, open]);

    const resetForm = () => {
        setFormData({
            workTypeId: "", description: "", tenderReferenceNo: "", date: "",
            tenderId: "", tenderTypeId: "", formOfContract: "",
            tenderFee: "", tenderValue: "", emd: ""
        });
        setAttachmentRows([{ id: Date.now(), description: "", file: null, fileName: "" }]);
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addRow = () => {
        setAttachmentRows([...attachmentRows, { id: Date.now(), description: "", file: null, fileName: "" }]);
    };

    const removeRow = (id) => {
        if (attachmentRows.length > 1) {
            setAttachmentRows(attachmentRows.filter(row => row.id !== id));
        }
    };

    const handleRowChange = (id, field, value) => {
        setAttachmentRows(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
    };

    const handleFileChange = (id, e) => {
        const file = e.target.files[0];
        if (file) {
            setAttachmentRows(prev => prev.map(row =>
                row.id === id ? { ...row, file: file, fileName: file.name } : row
            ));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const tenderDto = {
            ...formData,
            workTypeId: Number(formData.workTypeId),
            tenderTypeId: Number(formData.tenderTypeId),
            tenderFee: Number(formData.tenderFee),
            tenderValue: Number(formData.tenderValue),
            emd: Number(formData.emd),
            attachments: attachmentRows.map(row => ({
                id: row.id > 1000000000000 ? null : row.id,
                description: row.description.trim()
            }))
        };

        const finalFormData = new FormData();
        finalFormData.append("tender", new Blob([JSON.stringify(tenderDto)], { type: "application/json" }));

        attachmentRows.forEach((row) => {
            if (row.file) {
                finalFormData.append("files", row.file);
            } else {
                finalFormData.append("files", new Blob([], { type: "application/octet-stream" }));
            }
        });

        onSubmit(finalFormData);
    };

    if (!open) return null;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '1100px', maxWidth: '95%' }}>
                <div className="staff-modal-header">
                    <div className="staff-modal-title">{t("pageTitles.tenderProcessList")}</div>
                    <button className="admin-modal-close" onClick={onClose}><IoClose /></button>
                </div>
                <div className="staff-form-body">
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="form-group">
                                <label className="staff-modal-label">{t("tables.workName")} (Work Type) <span>*</span></label>
                                <select name="workTypeId" value={formData.workTypeId} onChange={handleFieldChange} className="staff-modal-input" required>
                                    <option value="">Select Work Name</option>
                                    {workTypes.map(w => <option key={w.id} value={w.id}>{w.workTypeName}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t("common.description")} <span>*</span></label>
                                <input name="description" placeholder="Enter Work Description" value={formData.description} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t("tables.tenderProcess.tenderReferenceNo")} <span>*</span></label>
                                <input name="tenderReferenceNo" placeholder="Enter Tender Reference Number" value={formData.tenderReferenceNo} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t("common.date")} <span>*</span></label>
                                <input type="date" name="date" value={formData.date} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t("tables.tenderProcess.tenderId")} <span>*</span></label>
                                <input name="tenderId" placeholder="Enter Tender ID" value={formData.tenderId} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t("tables.tenderProcess.tenderType")} <span>*</span></label>
                                <select name="tenderTypeId" value={formData.tenderTypeId} onChange={handleFieldChange} className="staff-modal-input" required>
                                    <option value="">Select Tender Type</option>
                                    {tenderTypes.map(t => <option key={t.id} value={t.id}>{t.tenderTypeName}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t("tables.tenderProcess.formOfContract")} <span>*</span></label>
                                <input name="formOfContract" placeholder="Enter Form Of Contract" value={formData.formOfContract} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t("tables.tenderProcess.tenderFee")} <span>*</span></label>
                                <input type="number" step="0.01" name="tenderFee" placeholder="0.00" value={formData.tenderFee} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t("tables.tenderProcess.tenderValue")} <span>*</span></label>
                                <input type="number" step="0.01" name="tenderValue" placeholder="0.00" value={formData.tenderValue} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t("tables.tenderProcess.emd")} (₹) <span>*</span></label>
                                <input type="number" step="0.01" name="emd" placeholder="0.00" value={formData.emd} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                        </div>

                        <div className="flex justify-between items-center mb-2">
                            <label className="staff-modal-label m-0 font-bold">{t("common.addAttachment")}</label>
                            <button type="button" onClick={addRow} className="staff-btn-save text-sm py-1 px-4">{t("common.addAttachment")}</button>
                        </div>

                        <table className="w-full border text-sm mb-4">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="border p-2">{t("common.description")}</th>
                                    <th className="border p-2">{t("common.attachments")}</th>
                                    <th className="border p-2 w-20">{t("common.delete")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attachmentRows.map(row => (
                                    <tr key={row.id}>
                                        <td className="border p-2">
                                            <input value={row.description} onChange={(e) => handleRowChange(row.id, 'description', e.target.value)} className="staff-modal-input w-full" placeholder="Enter Description" required />
                                        </td>
                                        <td className="border p-2">
                                            <input type="file" onChange={(e) => handleFileChange(row.id, e)} className="text-xs" />
                                            {row.fileName && <div className="text-blue-600 mt-1">{row.fileName}</div>}
                                        </td>
                                        <td className="border p-2 text-center">
                                            <button type="button" onClick={() => removeRow(row.id)} className="text-red-500 hover:text-red-700">
                                                <IoTrashOutline size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="flex justify-center mt-6">
                            <button type="submit" className="staff-btn-save px-12">{t("common.save")}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddStaffTenderProcessForm;